import React, { useState } from 'react';
import { PageContainer, ProCard, ProForm, ProFormDatePicker, ProFormDigit, ProFormSwitch, ProFormTextArea } from '@ant-design/pro-components';
import { Alert, Button, Typography, notification, Upload } from 'antd';
import { DownloadOutlined, InboxOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import axiosClient from '../api/axiosClient';
import dayjs from 'dayjs';

const { Paragraph, Text } = Typography;

const AssetIncomeReport: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const [fileList, setFileList] = useState<UploadFile[]>([]);

    const downloadTemplate = async () => {
        try {
            const response = await axiosClient.portal.get('/api/reports/asset-income/template', {
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'asset_income_tracks_template.xlsx');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error: any) {
            notification.error({
                message: 'Download failed',
                description: error.response?.data?.error || 'Could not download template'
            });
        }
    };

    const formatDateField = (v: unknown): string => {
        if (v == null || v === '') {
            return '';
        }
        const d = dayjs(v as dayjs.ConfigType);
        return d.isValid() ? d.format('YYYY-MM-DD') : '';
    };

    const handleSubmit = async (values: Record<string, unknown>) => {
        const identifiers = typeof values['identifiers'] === 'string' ? values['identifiers'].trim() : '';
        const hasFile = fileList.length > 0 && fileList[0].originFileObj;
        if (!hasFile && !identifiers) {
            notification.warning({ message: 'Upload an Excel file or paste ISRCs/UPCs' });
            return false;
        }

        const from = formatDateField(values['from']);
        const to = formatDateField(values['to']);
        const lifetime = !from && !to;
        if (!lifetime && (!from || !to)) {
            notification.warning({ message: 'Set both from and to, or leave both empty for lifetime income' });
            return false;
        }

        const formData = new FormData();
        if (hasFile) {
            formData.append('file', fileList[0].originFileObj!);
        }
        if (identifiers) {
            formData.append('identifiers', identifiers);
        }
        if (!lifetime) {
            formData.append('from', from);
            formData.append('to', to);
        }
        const customerId = values['customer_id'];
        if (typeof customerId === 'number' && customerId > 0) {
            formData.append('customer_id', String(customerId));
        }
        if (values['dm_income'] === true) {
            formData.append('dm_income', 'true');
        }

        try {
            setLoading(true);
            const response = await axiosClient.portal.post('/api/reports/asset-income/generate', formData, {
                responseType: 'blob'
            });

            const disposition = response.headers['content-disposition'] as string | undefined;
            let filename = lifetime ? 'asset_income_lifetime.xlsx' : `asset_income_${from}_${to}.xlsx`;
            if (disposition) {
                const match = /filename="?([^"]+)"?/.exec(disposition);
                if (match?.[1]) {
                    filename = match[1];
                }
            }

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            notification.success({ message: 'Report generated', description: 'Your download should start shortly.' });
        } catch (error: any) {
            let description = 'Failed to generate report';
            if (error.response?.data instanceof Blob) {
                try {
                    const text = await error.response.data.text();
                    const json = JSON.parse(text);
                    description = json.error || description;
                } catch {
                    /* ignore */
                }
            } else if (error.response?.data?.error) {
                description = error.response.data.error;
            }
            notification.error({ message: 'Error', description });
        } finally {
            setLoading(false);
        }
        return true;
    };

    return (
        <PageContainer
            title="Asset income report"
            subTitle="Revenue by ISRC or UPC (MD, Spotify, YouTube Music; music video optional via asset ID)">
            <ProCard>
                <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message="Music video revenue"
                    description={
                        <>
                            Youtube Music <Text strong>art track</Text> income is matched by ISRC/UPC.{' '}
                            <Text strong>Music video</Text> is <Text strong>not</Text> linked automatically — add a{' '}
                            <Text strong>Music Video Asset ID</Text> column in the Excel template (or leave blank to skip MV for that row).
                            Pasted identifiers only support ISRC/UPC; use the template for music video asset IDs.
                        </>
                    }
                />
                <Paragraph>
                    Provide <Text strong>ISRCs</Text> and/or <Text strong>UPCs</Text> via Excel or paste below. The report adds{' '}
                    <Text strong>track</Text> and <Text strong>album</Text> names from ClickHouse when possible (or from optional columns in the template).
                    Leave <Text strong>From</Text> and{' '}
                    <Text strong>To</Text> empty for <Text strong>lifetime</Text> income; set both dates for a period report with quarterly columns.
                </Paragraph>
                <Paragraph>
                    <Text strong>Podio customer ID</Text> limits rows to that customer or affiliate and uses split-label customer
                    revenue. <Text strong>DM income</Text> uses Digital Minds share (<Text code>OutDMRevenueEuro</Text>) instead of
                    customer revenue; Podio ID still filters which payment rows are included.
                </Paragraph>
                <Button type="link" icon={<DownloadOutlined />} onClick={downloadTemplate} style={{ marginBottom: 16 }}>
                    Download template
                </Button>
                <ProForm
                    layout="vertical"
                    onFinish={handleSubmit}
                    submitter={{
                        searchConfig: { submitText: 'Generate report' },
                        submitButtonProps: { loading }
                    }}>
                    <Upload.Dragger
                        accept=".xlsx"
                        multiple={false}
                        fileList={fileList}
                        beforeUpload={() => false}
                        onChange={({ fileList: fl }) => setFileList(fl)}
                        style={{ marginBottom: 24 }}>
                        <p className="ant-upload-drag-icon">
                            <InboxOutlined />
                        </p>
                        <p className="ant-upload-text">Optional: upload Excel (.xlsx) with ISRC, UPC, and Music Video Asset ID columns</p>
                    </Upload.Dragger>
                    <ProFormTextArea
                        name="identifiers"
                        label="ISRCs / UPCs (optional)"
                        placeholder={'GR-ABC-12-34567\n5210337001754\nGR-XYZ-99-99999, 1234567890123'}
                        fieldProps={{ rows: 6, style: { fontFamily: 'monospace' } }}
                        tooltip="Comma, semicolon, or newline separated. Each value is detected as ISRC or UPC."
                    />
                    <ProFormDatePicker
                        name="from"
                        label="From (optional)"
                        tooltip="Leave From and To empty for lifetime totals"
                        fieldProps={{ style: { width: '100%' } }}
                    />
                    <ProFormDatePicker
                        name="to"
                        label="To (optional)"
                        tooltip="Leave From and To empty for lifetime totals"
                        fieldProps={{ style: { width: '100%' } }}
                    />
                    <ProFormDigit
                        name="customer_id"
                        label="Podio customer ID (optional)"
                        min={0}
                        fieldProps={{ precision: 0 }}
                        tooltip="Affiliate / split-label filter; leave empty for all rows matching ISRC/UPC"
                    />
                    <ProFormSwitch
                        name="dm_income"
                        label="DM income"
                        tooltip="Sum OutDMRevenueEuro (our share) instead of customer revenue"
                    />
                </ProForm>
            </ProCard>
        </PageContainer>
    );
};

export default AssetIncomeReport;
